import { styled } from 'baseui'
import type { Theme } from 'baseui'
import type { StyleObject } from 'styletron-standard'
import { pulseLighting } from '@/shared/theme/pulseLighting'

interface ThemeProps {
  $theme: Theme
}

interface TabProps extends ThemeProps {
  $isActive: boolean
}

const pulseTabBackground = ($theme: Theme, $isActive: boolean) => ($isActive ? $theme.colors.white : 'transparent')

const pulseTabStyle = ({ $theme, $isActive }: TabProps): StyleObject => ({
  height: '32px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '14px',
  paddingRight: '14px',
  gap: '6px',
  borderTopLeftRadius: '7px',
  borderTopRightRadius: '7px',
  borderBottomLeftRadius: '7px',
  borderBottomRightRadius: '7px',
  background: pulseTabBackground($theme, $isActive),
  color: $isActive ? $theme.colors.textPulseStrong : $theme.colors.textPulseLabel,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: $isActive ? 600 : 500,
  whiteSpace: 'nowrap',
  boxShadow: $isActive ? pulseLighting($theme).shadowPulseTab : 'none',
  ':hover': {
    background: $isActive ? $theme.colors.white : $theme.colors.bgPulseTrack,
  },
})

const pulseDirtyTabStyle = ({ $theme }: ThemeProps): StyleObject => {
  const { shadowPulseDirtyTab, shadowPulseDirtyRingStart, shadowPulseDirtyRingEnd } = pulseLighting($theme)

  return {
    background: $theme.colors.dangerPulse,
    color: $theme.colors.white,
    fontWeight: 600,
    boxShadow: shadowPulseDirtyTab,
    animationName: {
      '0%': { boxShadow: shadowPulseDirtyRingStart },
      '70%': { boxShadow: shadowPulseDirtyRingEnd },
      '100%': { boxShadow: shadowPulseDirtyRingEnd },
    },
    animationDuration: '1.8s',
    animationTimingFunction: 'ease',
    animationIterationCount: 'infinite',
    ':hover': {
      background: $theme.colors.dangerPulse,
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  }
}

export const pulseTabsOverrides = {
  TabBorder: {
    style: { height: 0 },
  },
  TabHighlight: {
    style: { display: 'none' },
  },
  TabBar: {
    style: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      rowGap: '8px',
    },
  },
  TabList: {
    style: ({ $theme }: ThemeProps) => ({
      flexGrow: 0,
      gap: '4px',
      paddingTop: '4px',
      paddingBottom: '4px',
      paddingLeft: '4px',
      paddingRight: '4px',
      marginBottom: 0,
      backgroundColor: $theme.colors.bgPulseTabs,
      borderTopWidth: '1px',
      borderBottomWidth: '1px',
      borderLeftWidth: '1px',
      borderRightWidth: '1px',
      borderTopStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      borderTopColor: $theme.colors.borderPulse,
      borderBottomColor: $theme.colors.borderPulse,
      borderLeftColor: $theme.colors.borderPulse,
      borderRightColor: $theme.colors.borderPulse,
      borderTopLeftRadius: '10px',
      borderTopRightRadius: '10px',
      borderBottomLeftRadius: '10px',
      borderBottomRightRadius: '10px',
    }),
  },
  EndEnhancerContainer: {
    style: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 'auto',
      justifyContent: 'flex-start',
      gap: '14px',
      marginLeft: '8px',
    },
  },
}

const pulseAdditionalSalesTabPanelStyle = ({ $theme }: ThemeProps): StyleObject => ({
  backgroundColor: $theme.colors.white,
  borderTopLeftRadius: '0.5rem',
  borderTopRightRadius: '0.5rem',
  borderBottomLeftRadius: '0.5rem',
  borderBottomRightRadius: '0.5rem',
  marginTop: '14px',
  paddingTop: '24px',
  paddingBottom: '24px',
  paddingLeft: '24px',
  paddingRight: '24px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '48px',
  alignSelf: 'stretch',
})

const pulseCanvasTabPanelStyle: StyleObject = {
  backgroundColor: 'transparent',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
}

export const pulseTabOverrides = {
  Tab: { style: pulseTabStyle },
  TabPanel: { style: pulseAdditionalSalesTabPanelStyle },
}

export const pulseContractTabOverrides = {
  Tab: { style: pulseTabStyle },
  TabPanel: { style: pulseCanvasTabPanelStyle },
}

export const pulseClientTabOverrides = (hasUnsavedChanges: boolean) => ({
  Tab: {
    style: (props: TabProps) => ({
      ...pulseTabStyle(props),
      ...(hasUnsavedChanges ? pulseDirtyTabStyle(props) : {}),
    }),
  },
  TabPanel: { style: pulseCanvasTabPanelStyle },
})

export const pulseAddButtonOverrides = {
  BaseButton: {
    style: ({ $theme }: ThemeProps) => ({
      height: '42px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '16px',
      paddingRight: '16px',
      backgroundColor: $theme.colors.white,
      color: $theme.colors.textPulseStrong,
      fontSize: '14px',
      fontWeight: 500,
      borderTopWidth: '1px',
      borderBottomWidth: '1px',
      borderLeftWidth: '1px',
      borderRightWidth: '1px',
      borderTopStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderRightStyle: 'solid',
      borderTopColor: $theme.colors.borderPulse,
      borderBottomColor: $theme.colors.borderPulse,
      borderLeftColor: $theme.colors.borderPulse,
      borderRightColor: $theme.colors.borderPulse,
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
      ':hover': {
        backgroundColor: $theme.colors.bgPulseCanvas,
        color: $theme.colors.textPulseStrong,
      },
    }),
  },
}

export const TabsFrame = styled<'div', { $pulse: boolean }>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        backgroundColor: $theme.colors.bgPulseCanvas,
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        paddingTop: '18px',
        paddingBottom: '24px',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
      }
    : {},
)

export const PulseClientTabTitleText = styled('span', {
  display: 'inline-flex',
  flexDirection: 'row-reverse',
  alignItems: 'center',
  gap: '6px',
})

export const PulseUnsavedIcon = styled('span', {
  display: 'inline-flex',
  lineHeight: 0,
  ':focus-visible': {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'currentColor',
    outlineOffset: '2px',
  },
})

export const ClientTabColumns = styled<'div', { $pulse: boolean; $columns: number }>('div', ({ $pulse, $columns }) =>
  $pulse
    ? {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(${$columns}, minmax(0, 1fr))`,
        gap: '20px',
        alignItems: 'start',
        marginTop: '16px',
      }
    : {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '48px',
      },
)

export const ClientTabSideColumns = styled<'div', { $stacked: boolean }>('div', ({ $stacked }) =>
  $stacked ? { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 } : { display: 'contents' },
)

export const ClientTabColumn = styled<'div', { $pulse: boolean }>('div', ({ $pulse }) =>
  $pulse ? { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 } : { width: '100%' },
)
